import {
  Component,
  inject,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import { Params } from '@angular/router';
import { RouterModule } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { LayoutService } from '../../services/layout.service';

type SidebarItem = {
  route: string[];
  icon: string;
  key: string;
  showChevron?: boolean;
  queryParams?: Params;
  children?: SidebarItem[];
};

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule, TranslocoDirective],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
  private readonly layoutService = inject(LayoutService);
  readonly mobileSidebarOpen = this.layoutService.sidebarOpen;
  readonly desktopSidebarCollapsed = this.layoutService.desktopSidebarCollapsed;
  private readonly expandedGroups = signal<Record<string, boolean>>({
    'layout.sidebar.study': false,
    'layout.sidebar.explore': false,
  });

  readonly primaryItems: SidebarItem[] = [
    {
      route: ['/dashboard'],
      icon: 'fas fa-house',
      key: 'layout.sidebar.dashboard',
    },
    {
      route: ['/passage'],
      icon: 'fas fa-book-open',
      key: 'layout.sidebar.readBible',
    },
    {
      route: ['/dashboard'],
      icon: 'fas fa-bell',
      key: 'layout.sidebar.study',
      showChevron: true,
      children: [
        {
          route: ['/dashboard'],
          queryParams: { tab: 'ai-study-companion' },
          icon: 'fa-solid fa-wand-magic-sparkles',
          key: 'layout.sidebar.aiStudyCompanion',
        },
        {
          route: ['/dashboard'],
          queryParams: { tab: 'verse-of-the-day' },
          icon: 'fa-solid fa-book-bible',
          key: 'layout.sidebar.verseOfDay',
        },
        {
          route: ['/dictionary'],
          icon: 'fa-solid fa-book-bookmark',
          key: 'layout.sidebar.dictionary',
        },
        {
          route: ['/coming-soon', 'study-plans'],
          icon: 'fa-solid fa-list-check',
          key: 'layout.sidebar.studyPlans',
        },
      ],
    },
    {
      route: ['/theme'],
      icon: 'fas fa-circle-chevron-up',
      key: 'layout.sidebar.explore',
      showChevron: true,
      children: [
        {
          route: ['/theme'],
          icon: 'fa-solid fa-compass',
          key: 'layout.sidebar.themes',
        },
        {
          route: ['/search'],
          icon: 'fa-solid fa-magnifying-glass',
          key: 'layout.sidebar.search',
        },
        {
          route: ['/coming-soon', 'bible-books'],
          icon: 'fa-solid fa-book-open',
          key: 'layout.sidebar.bibleBooks',
        },
      ],
    },
    {
      route: ['/coming-soon', 'community'],
      icon: 'fas fa-user-group',
      key: 'layout.sidebar.community',
    },
    {
      route: ['/coming-soon', 'notes'],
      icon: 'fas fa-note-sticky',
      key: 'layout.sidebar.notes',
    },
  ];

  readonly secondaryItems: SidebarItem[] = [
    {
      route: ['/profile'],
      icon: 'fas fa-user',
      key: 'layout.sidebar.profile',
    },
    {
      route: ['/settings'],
      icon: 'fas fa-gear',
      key: 'layout.sidebar.settings',
    },
    {
      route: ['/coming-soon', 'subscription'],
      icon: 'fas fa-circle-play',
      key: 'layout.sidebar.subscription',
    },
  ];

  closeMobileSidebar(): void {
    this.layoutService.closeSidebar();
  }

  toggleDesktopSidebar(): void {
    this.layoutService.toggleDesktopSidebar();
  }

  toggleGroup(item: SidebarItem): void {
    if (!item.children?.length) {
      return;
    }

    this.expandedGroups.update((value) => ({
      ...value,
      [item.key]: !value[item.key],
    }));
  }

  isGroupExpanded(item: SidebarItem): boolean {
    return !!this.expandedGroups()[item.key];
  }
}
